import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthState } from 'react-firebase-hooks/auth';
import { auth } from '../config/firebase';
import { seatService } from '../services/api.service';
import './SelectedSeatsInfo.css';
import CheckoutModal from './CheckoutModal';

const SelectedSeatsInfo = ({ selectedSeats, seatingData, event, reservationId }) => {
    const navigate = useNavigate();
    const [user] = useAuthState(auth);
    const [showCheckoutModal, setShowCheckoutModal] = useState(false);

    if (!selectedSeats.length) {
        return null;
    }

    const getSelectedSeatsDetails = () => {
        return selectedSeats.map(seat => {
            // If seat is already an object with all required info, return it
            if (typeof seat === 'object' && seat.label && seat.category && seat.price) {
                return seat;
            }

            // Otherwise, find the seat in seating data
            const seatLabel = typeof seat === 'string' ? seat : seat.label;
            const seatInfo = seatingData.seat_map.find(s => {
                const labelData = JSON.parse(s.row.label);
                return `${labelData.row_label}${labelData.seats[0].number}` === seatLabel;
            });

            if (!seatInfo) {
                console.error(`Seat not found: ${seatLabel}`);
                return null;
            }

            // Find ticket type for this seat's price category
            const ticketType = seatingData.ticket_types.find(
                type => type.price_category === seatInfo.price_category
            );

            if (!ticketType) {
                console.error(`Ticket type not found for category: ${seatInfo.price_category}`);
                return null;
            }

            return {
                label: seatLabel,
                category: seatInfo.price_category,
                price: ticketType.price
            };
        }).filter(Boolean);
    };

    const selectedSeatsDetails = getSelectedSeatsDetails();
    const totalPrice = selectedSeatsDetails.reduce((sum, seat) => sum + seat.price, 0);

    const simplifiedEvent = {
        id: event.id,
        title: event.title,
        date: event.date,
        time: event.time,
        location: {
            venue: event.location?.venue || '',
            city: event.location?.city || ''
        },
        image_url: event.image_url,
        sub_events: [{
            id: event.sub_events[0]?.id || event.sub_events[0]?._id,
            _id: event.sub_events[0]?.id || event.sub_events[0]?._id,
            date: event.sub_events[0]?.date,
            location: {
                venue: event.location?.venue || '',
                city: event.location?.city || ''
            }
        }]
    };

    const handleProceedToCheckout = async () => {
        try {
            window.isNavigatingToCheckout = true;
            window.isNavigatingToSeating = true;
            const currentEventId = event.id;
            const currentSubEventId = event.sub_events[0]._id;
            
            // Store the current path
            sessionStorage.setItem('lastSeatingPath', `/seating/${currentEventId}/${currentSubEventId}`);
            
            // Get the selected seats with their details
            const seatsWithDetails = selectedSeatsDetails;

            const paymentDetails = {
                event: simplifiedEvent,
                selectedSeats: seatsWithDetails,
                totalAmount: totalPrice,
                ticketCounts: {
                    [currentEventId]: selectedSeats.length
                },
                reservationId,
                originalPath: `/seating/${currentEventId}/${currentSubEventId}`,
                preserveSeats: true
            };

            if (user) {
                navigate('/checkout', {
                    state: paymentDetails
                });
            } else {
                setShowCheckoutModal(true);
            }
        } catch (error) {
            console.error('Failed to proceed to checkout:', error);
        }
    };

    const handleLogin = () => {
        navigate('/login', { 
            state: { 
                returnTo: '/checkout',
                paymentDetails: {
                    event: simplifiedEvent,
                    selectedSeats: selectedSeatsDetails,
                    totalAmount: totalPrice,
                    ticketCounts: {
                        [event.id]: selectedSeats.length
                    },
                    reservationId,
                    originalPath: `/seating/${event.id}/${event.sub_events[0]._id}`
                }
            }
        });
    };

    const handleGuestCheckout = () => {
        window.isNavigatingToCheckout = true;
        window.isNavigatingToSeating = true;
        navigate('/checkout', {
            state: {
                event: simplifiedEvent,
                selectedSeats: selectedSeatsDetails,
                totalAmount: totalPrice,
                ticketCounts: {
                    [event.id]: selectedSeats.length
                },
                reservationId,
                originalPath: `/seating/${event.id}/${event.sub_events[0]._id}`,
                preserveSeats: true
            },
            replace: true
        });
    };

    return (
        <div className="selected-seats-info">
            <h3>Seçilen Koltuklar</h3>
            <div className="selected-seats-list">
                {selectedSeatsDetails.map(seat => (
                    <div key={seat.label} className="selected-seat-item">
                        <span className="seat-label">Koltuk {seat.label}</span>
                        <span className="seat-category">{seat.category}</span>
                        <span className="seat-price">{seat.price} TL</span>
                    </div>
                ))}
            </div>
            <div className="total-price">
                <span>Toplam</span>
                <span className="price">{totalPrice} TL</span>
            </div>
            <button 
                className="proceed-button"
                onClick={handleProceedToCheckout}
            >
                Ödemeye Geç
            </button>

            <CheckoutModal
                isOpen={showCheckoutModal}
                onClose={() => setShowCheckoutModal(false)}
                onLogin={handleLogin}
                onGuestCheckout={handleGuestCheckout}
            />
        </div>
    );
};

export default SelectedSeatsInfo; 